/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import Slide from '@mui/material/Slide';

import { Portal } from '../Portal';
import { Box } from '../Box';
import { Button } from '../Button';
import { CircularProgress } from '../CircularProgress';
import { Dialog } from '../Dialog';
import { Typography } from '../Typography';
import { useIsMobile } from '../hooks';
import { pluralize } from '../format';
import { Link } from '../Link';
import { Icon } from '../Icon';

import { DownloadNotificationItem } from './DownloadNotification.types';
import { useStyles } from './DownloadNotification.styles';

/**
 * Свойства компонента {@link DownloadNotification}
 */
interface DownloadNotificationProps {
  /**
   * Если true, уведомление видимо.
   *
   * @defaultValue `true`
   */
  show?: boolean;

  /**
   * Список уведомлений о загрузке.
   */
  notifications: DownloadNotificationItem[];

  /**
   * Функция обратного вызова для обработки закрытия уведомления.
   */
  onClose: () => void;
}

/**
 * Компонент для отображения уведомления о загрузке.
 *
 * @public
 * @param show - Если true, уведомление видимо.
 * @param notifications - Список уведомлений о загрузке.
 * @param onClose - Функция обратного вызова для обработки закрытия уведомления.
 * @returns  JSX-элемент, представляющий уведомление о загрузке, если оно открыто, иначе null.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DownloadNotificationProps}
 */
export const DownloadNotification = ({
  show = true,
  notifications,
  onClose,
}: DownloadNotificationProps) => {
  const { classes } = useStyles();

  const isMobile = useIsMobile();

  const downloads = notifications || [];

  const [isDialogOpened, setIsDialogOpened] = useState(false);

  const handleClose = () => {
    if (downloads.filter((item) => item.status === 'waiting').length) {
      setIsDialogOpened(true);
    } else {
      onClose();
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpened(false);
  };

  const handleCancelDownloads = () => {
    handleCloseDialog();
    onClose();
  };

  useEffect(() => {
    const webimButtonCorner = Array.from(
      document.getElementsByClassName('webim-button-corner'),
    )[0];

    if (webimButtonCorner) {
      if (downloads && !!downloads.length) {
        webimButtonCorner.classList.add('webim-button-corner-right');
      } else {
        webimButtonCorner.classList.remove('webim-button-corner-right');
      }
    }

    const webimChat = document.getElementById('webim_chat');
    if (webimChat) {
      if (downloads && !!downloads.length) {
        webimChat.classList.add('webim-chat-right');
      } else {
        webimChat.classList.remove('webim-chat-right');
      }
    }

    return () => {
      if (webimButtonCorner) {
        webimButtonCorner.classList.remove('webim-button-corner-right');
      }

      if (webimChat) {
        webimChat.classList.remove('webim-chat-right');
      }
    };
  }, [downloads && !!downloads.length]);

  if (!show) {
    return null;
  }

  return (
    <Portal>
      <div className={classes.notifications}>
        <Dialog
          fullScreen={isMobile}
          onClose={handleCloseDialog}
          open={isDialogOpened}
        >
          <Box
            flexGrow={1}
            px={isMobile ? '16' : '48'}
            pt={isMobile ? '32' : '48'}
          >
            {isMobile && (
              <Box mb="8">
                <Link size="small" onClick={handleCloseDialog} stroke={false}>
                  Назад
                </Link>
              </Box>
            )}
            <Box mb={isMobile ? '20' : '48'}>
              <Typography variant="h5">Отменить скачивание?</Typography>
            </Box>
            <Box mb="48">
              <Typography color="text.light.tertirary">
                Загрузка документов не завершена. Все равно отменить?
              </Typography>
            </Box>
          </Box>
          <Box
            p="16"
            display="flex"
            justifyContent="flex-end"
            columnGap={isMobile ? 1 : 2}
            bgcolor={!isMobile ? 'bg.greyscale.primary' : undefined}
          >
            <Button
              variant={isMobile ? 'quaternary' : 'tertiaryLight'}
              onClick={handleCancelDownloads}
              size="large"
              fullWidth={isMobile}
            >
              Отменить
            </Button>
            <Button
              fullWidth={isMobile}
              size="large"
              onClick={handleCloseDialog}
            >
              {isMobile ? 'Продолжить' : 'Продолжить скачивание'}
            </Button>
          </Box>
        </Dialog>
        <div className={classes.stack}>
          {downloads && !!downloads.length && (
            <Slide direction="left" in mountOnEnter unmountOnExit>
              <Box mb="8" className={classes.wrapper}>
                <Box
                  p="16"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  className={classes.content}
                >
                  <Box ml="8" mr="20">
                    <Typography
                      className={classes.text}
                      color="text.dark.primary"
                      variant="body3"
                    >
                      Подготовка к скачиванию
                    </Typography>
                  </Box>
                  <Button
                    variant="tertiaryDark"
                    onClick={handleClose}
                    icon={<Icon name="cancel_outline" size="24" />}
                  />
                </Box>
                <Box
                  className={classes.inner}
                  bgcolor="bg.light.primary"
                  py="8"
                  display="flex"
                  flexDirection="column"
                >
                  {downloads.map((item) => (
                    <Box px="24" py="8" key={item.id}>
                      {item.status === 'waiting' && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          {item.text?.loading || (
                            <span>
                              Создаем архив (
                              {pluralize(item.counter, [
                                'документ',
                                'документа',
                                'документов',
                              ])}
                              )
                            </span>
                          )}
                          <CircularProgress size={15} />
                        </Box>
                      )}
                      {item.status === 'complete' && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          {item.text?.complete || (
                            <span>
                              Архив создан (
                              {pluralize(item.counter, [
                                'документ',
                                'документа',
                                'документов',
                              ])}
                              )
                            </span>
                          )}
                          <Icon
                            name="check_circle_on"
                            size="24"
                            color="original.brand.primary"
                          />
                        </Box>
                      )}
                      {item.status === 'failed' && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          {item.text?.failed || (
                            <span>
                              Ошибка создания архива (
                              {pluralize(item.counter, [
                                'документ',
                                'документа',
                                'документов',
                              ])}
                              )
                            </span>
                          )}
                          <Icon
                            name="info_circle"
                            size="24"
                            color="accent.text.error"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Slide>
          )}
        </div>
      </div>
    </Portal>
  );
};
